import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  CartesianGrid, ComposedChart, Legend, Line,
  Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchResponseRateText,
  fetchResponseRateEmail,
} from "../../../services/insightModule/insightService";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import { STRINGS } from "../../../common/constants/commonConstants";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";

// ── Helpers ────────────────────────────────────────────────────────
// Parse YYYY-MM-DD as local date, not UTC.
// new Date("2026-04-25") is UTC midnight → lands on the previous day in
// negative-offset timezones.
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function fmt(n, dec = 2) {
  const num = Number(n);
  return isNaN(num) ? "—" : num.toFixed(dec);
}

// ResponseRate may come as "0.34", "34%", 0.34, or 34
function parseRatePct(v) {
  if (v == null) return 0;
  const s = String(v).trim();
  if (!s) return 0;
  if (s.endsWith("%")) {
    const n = Number(s.replace("%", "").trim());
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return (n >= 0 && n <= 1) ? n * 100 : n;
}

function filterByDate(rows, startDate, endDate) {
  const start = startDate
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)
    : null;
  const end = endDate
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999)
    : null;
  return (rows || []).filter(row => {
    const d = parseLocalDate(row.ConversationDate);
    if (!d || isNaN(d.getTime())) return false;
    if (start && d < start) return false;
    if (end   && d > end)   return false;
    return true;
  });
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE }) {
  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 14px", minWidth: 0, flex: 1,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const ResponseRate = () => {
  const today        = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate,  setStartDate]  = useState(firstOfMonth);
  const [endDate,    setEndDate]    = useState(today);
  const [channel,    setChannel]    = useState("Texts");
  const [allText,    setAllText]    = useState([]);
  const [allEmail,   setAllEmail]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [page,       setPage]       = useState(0);
  const rowsPerPage = 25;

  // ── Fetch both channels once ───────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, e] = await Promise.all([
        fetchResponseRateText(),
        fetchResponseRateEmail(),
      ]);
      setAllText(Array.isArray(t)  ? t : []);
      setAllEmail(Array.isArray(e) ? e : []);
    } catch (err) {
      console.error(err);
      setError("Failed to load response rate data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, []);

  // ── Apply date filter client-side ─────────────────────────────
  const data = useMemo(() => {
    const raw = channel === "Emails" ? allEmail : allText;
    return filterByDate(raw, startDate, endDate);
  }, [channel, allText, allEmail, startDate, endDate]);

  // ── KPIs ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const contacted  = data.reduce((a,b) => a + safeNum(b.CustomersContacted),   0);
    const responded  = data.reduce((a,b) => a + safeNum(b.CustomersWhoResponded), 0);
    const rates      = data.map(r => parseRatePct(r.ResponseRate)).filter(n => n > 0);
    const avgRate    = rates.length ? rates.reduce((a,b) => a+b, 0) / rates.length : 0;
    const maxRate    = rates.length ? Math.max(...rates) : 0;
    const minRate    = rates.length ? Math.min(...rates) : 0;
    return { contacted, responded, avgRate, maxRate, minRate,
      calcRate: contacted > 0 ? responded / contacted * 100 : 0 };
  }, [data]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    [...data]
      .sort((a, b) => {
        const da = parseLocalDate(a.ConversationDate);
        const db = parseLocalDate(b.ConversationDate);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      })
      .map(r => ({
        date:      fmtDate(r.ConversationDate),
        contacted: safeNum(r.CustomersContacted),
        responded: safeNum(r.CustomersWhoResponded),
        rate:      parseRatePct(r.ResponseRate),
      }))
  , [data]);

  // ── Pagination ─────────────────────────────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a.ConversationDate);
      const db = parseLocalDate(b.ConversationDate);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  const COLS    = "100px 130px 150px 110px";
  const HEADERS = ["Date", "Contacted", "Responded", "Response Rate"];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d && (!startDate || d >= startDate)) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => setPage(0)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => { setChannel(ch); setPage(0); }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="Customers contacted"  value={kpis.contacted.toLocaleString()}  color={BLUE}  />
            <KpiCard label="Customers responded"  value={kpis.responded.toLocaleString()}  color={GREEN} />
            <KpiCard label="Avg response rate"    value={`${fmt(kpis.avgRate)}%`}           color={GREEN} />
            <KpiCard label="Calc response rate"   value={`${fmt(kpis.calcRate)}%`}          color={BLUE}  />
            <KpiCard label="Max response rate"    value={`${fmt(kpis.maxRate)}%`}           color={AMBER} />
            <KpiCard label="Min response rate"    value={`${fmt(kpis.minRate)}%`}           color={PINK}  />
          </Box>

          {/* ── RESPONSE RATE OVER TIME ───────────────────────── */}
          <SectionTitle>Response rate over time</SectionTitle>

          <ChartCard height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <defs>
                  <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={GREEN} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={GREEN} stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis yAxisId="left"  tick={axisStyle} />
                <YAxis yAxisId="right" orientation="right"
                  tick={{ ...axisStyle, fill: GREEN }}
                  tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) =>
                    n === "Response rate %" ? [`${fmt(v)}%`, n] : [v, n]
                  }
                />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="contacted" name="Contacted" fill={BLUE}  radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="responded" name="Responded" fill={AMBER} radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="rate"
                  name="Response rate %" stroke={GREEN}
                  dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={h} sx={{ textAlign: i === 0 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const ratePct  = parseRatePct(row.ResponseRate);
              const rateColor = ratePct >= 50 ? GREEN : ratePct >= 25 ? AMBER : PINK;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                }}>
                  <Box>{fmtDate(row.ConversationDate)}</Box>
                  <Box sx={{ textAlign: "right" }}>
                    {safeNum(row.CustomersContacted).toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: GREEN, fontWeight: 500 }}>
                    {safeNum(row.CustomersWhoResponded).toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: rateColor, fontWeight: 500 }}>
                    {row.ResponseRate != null
                      ? (String(row.ResponseRate).includes("%")
                          ? row.ResponseRate
                          : `${fmt(ratePct)}%`)
                      : "—"}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ResponseRate;